import 'core-js/stable';
import 'intersection-observer';
import 'regenerator-runtime/runtime';
import Vue from 'vue';
import axios, { AxiosResponse } from 'axios';
import VueAxios from 'vue-axios';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import InitData from '@/models/InitData';
import InitDataExtended from '@/models/InitDataExtended';
import SearchDefaults from '@/types/SearchDefaults';
import ValidateRouteData from '@/rules/ValidateRouteData';
import SupportedContext from '@/types/SupportedContext';
import SupportedCountry from '@/types/SupportedCountry';
import SupportedLocale from '@/types/SupportedLocale';
import { Route } from 'vue-router';
import { BaseAction, GeneralAction } from '@/store/actions';
import GenericResponse from '@/models/GenericResponse';
import HtmlDataHelper from '@/helpers/HtmlDataHelper';
import TileTrackerHelper from '@/helpers/TileTrackerHelper';
import App from './App.vue';
import router from './router';
import store from './store';
import i18n from './i18n';

// Install plugins & modules
Vue.use(VueAxios, axios);

// Layout-init
Vue.component('default-layout', DefaultLayout);


Vue.config.productionTip = false;

// Add a request interceptor
axios.interceptors.request.use(
  async (config) => {
    await store.dispatch(BaseAction.ChangeActiveRequests, { with: 1 });
    return config;
  },
  async (error) => Promise.reject(error),
);

// Add a response interceptor
axios.interceptors.response.use(async (response: AxiosResponse<GenericResponse>) => {
  await store.dispatch(BaseAction.ChangeActiveRequests, { with: -1 });
  return Promise.resolve(response);
}, async (error) => {
  if (error.response.status === 401 || error.response.status === 403) {
    await store.dispatch(BaseAction.ChangeActiveRequests, { with: -1 });
    await store.dispatch(BaseAction.AddWarning, { code: `server${error.response.status}` });
  } else {
    await store.dispatch(
      BaseAction.AddError,
      { code: `server${error.response.status}` },
    );
  }
  return Promise.reject(error);
});

const mountId = 'searchPagePlaceholder';

let initData: InitData;
const readInitData = (elId: string): InitData => {
  const mountEl: HTMLElement | null = document.getElementById(elId);
  if (mountEl === null) {
    throw new Error('Mount HTML element must exist!');
  }

  const parseInitData = (input: {
    [name: string]: string | number;
  }): InitData | false => {
    const schema: Record<keyof InitData, string> = {
      country: 'string',
      locale: 'string',
      searchContext: 'string',
      searchTerm: 'string',
      searchIncrement: 'number',
      categoriesIncrement: 'number',
      productsIncrement: 'number',
      externalIncrement: 'number',
      displayFilters: 'boolean',
    };

    const missingProperties = Object.keys(schema)
      .filter((key) => input[key] === undefined)
      .map((key) => key as keyof InitData)
      .map((key) => new Error(`Document is missing ${key} ${schema[key]}`));

    if (missingProperties.length > 0) {
      return false;
    }

    return {
      country: input.country as SupportedCountry,
      locale: input.locale as SupportedLocale,
      searchContext: input.searchContext as SupportedContext,
      searchTerm: input.searchTerm as string,
      searchIncrement: input.searchIncrement as number,
      categoriesIncrement: input.categoriesIncrement as number,
      productsIncrement: input.productsIncrement as number,
      externalIncrement: input.externalIncrement as number,
      displayFilters: input.displayFilters === 'true',
    };
  };

  const { dataset } = mountEl;
  const domInitData: { [name: string]: string | number } = { searchIncrement: 0, ...dataset };

  if (typeof (dataset.searchIncrement) === 'string') {
    domInitData.searchIncrement = parseInt(dataset.searchIncrement, 10);
  }
  if (typeof (dataset.categoriesIncrement) === 'string') {
    domInitData.categoriesIncrement = parseInt(dataset.categoriesIncrement, 5);
  }
  if (typeof (dataset.productsIncrement) === 'string') {
    domInitData.productsIncrement = parseInt(dataset.productsIncrement, 8);
  }
  if (typeof (dataset.externalIncrement) === 'string') {
    domInitData.externalIncrement = parseInt(dataset.externalIncrement, 5);
  }

  const parsedInitData = parseInitData(domInitData);

  return parsedInitData || {
    country: SearchDefaults.country,
    locale: SearchDefaults.locale,
    searchTerm: '',
    searchContext: SearchDefaults.searchContext,
    searchIncrement: SearchDefaults.increment,
    categoriesIncrement: SearchDefaults.categoriesIncrement,
    productsIncrement: SearchDefaults.productsIncrement,
    externalIncrement: SearchDefaults.externalIncrement,
    displayFilters: SearchDefaults.displayFilters,
  };
};

function initTracking() {
  const tileTracker = new TileTrackerHelper(new HtmlDataHelper());

  window.tileTracker = tileTracker;

  document.addEventListener('contentFiltering', () => {
    tileTracker.sendAllData();
    tileTracker.clearTracking();
    HtmlDataHelper.clearHtml();
    tileTracker.runTracking();
  });

  window.addEventListener('load', () => {
    tileTracker.runTracking();
  });

  window.addEventListener('resize', () => {
    tileTracker.runTracking();
  });

  window.addEventListener('scroll', () => {
    tileTracker.runTracking();
  });

  window.addEventListener('beforeunload', () => {
    tileTracker.sendAllData();
  });
}

declare global {
  interface Window {
    dataLayer?: {
      push: Function;
    };
    Foundation?: Function;
    jQuery?: Function;
    components: {
      import: Function;
      loadAsFragment: Function;
    };
    tileTracker: TileTrackerHelper;
  }
}
Vue.config.ignoredElements = [/^fa-/];
new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App),
  beforeMount() {
    initData = readInitData(mountId);
    initTracking();
  },
  async mounted() {
    const validateRouteData = ValidateRouteData.validate({
      searchContext: initData.searchContext,
      query: { ...{ ...router.currentRoute.query, q: initData.searchTerm } },
    });
    await this.$store.dispatch(GeneralAction.Init, {
      locale: initData.locale,
      country: initData.country,
      searchTerm: validateRouteData.searchTerm,
      searchContext: validateRouteData.searchContext,
      searchIncrement: initData.searchIncrement,
      categoriesIncrement: initData.categoriesIncrement,
      productsIncrement: initData.productsIncrement,
      externalIncrement: initData.externalIncrement,
      filterSerializedOptions: validateRouteData.filterSerializedOptions,
      displayFilters: initData.displayFilters,
    } as InitDataExtended);

    if (router.currentRoute.fullPath === '/') {
      await router.push({
        name: 'Search',
        params: { searchContext: validateRouteData.searchContext, country: initData.country },
        query: { q: validateRouteData.searchTerm },
      });
    }
  },
  watch: {
    $route: async (to: Route) => {
      if (to.name === 'Search') {
        await store.dispatch(GeneralAction.RouteUpdate, {
          routeData: ValidateRouteData.validate({
            searchContext: to.params.searchContext,
            query: to.query,
          }),
        });
      }
    },
  },
}).$mount(`#${mountId}`);
